import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuthStore } from '@/stores/auth-store'
import { triggerRevalidation } from '@/lib/utils'
import { useTenantChekiGroupSetting, useTenantChekiDefaultPrices } from '@/hooks/use-cheki'
import MediaPicker from '@/components/media/media-picker'
import { Image } from 'lucide-react'
import { FeatureGate } from '@/components/feature-gate'

type Tab = 'payment' | 'cheki_grup' | 'harga_cheki'

// ── Tab: Info Pembayaran ───────────────────────────────────────────────────────

function PaymentTab() {
  const tenantId = useAuthStore((s) => s.currentTenant?.id)
  const qc = useQueryClient()
  const [paymentInfo, setPaymentInfo] = useState('')
  const [qrisImageUrl, setQrisImageUrl] = useState<string | null>(null)
  const [qrisImageAssetId, setQrisImageAssetId] = useState<string | null>(null)
  const [pickerOpen, setPickerOpen] = useState(false)
  const [saved, setSaved] = useState(false)

  const { data: tenant, isLoading } = useQuery({
    queryKey: ['tenant-payment-info', tenantId],
    enabled: !!tenantId,
    queryFn: async () => {
      const { data, error } = await (supabase
        .from('tenants')
        .select('payment_info, qris_image_url, qris_image_asset_id') as any)
        .eq('id', tenantId!)
        .single()
      if (error) throw error
      return data as {
        payment_info: string | null
        qris_image_url: string | null
        qris_image_asset_id: string | null
      }
    },
  })

  useEffect(() => {
    if (tenant) {
      setPaymentInfo(tenant.payment_info ?? '')
      setQrisImageUrl(tenant.qris_image_url ?? null)
      setQrisImageAssetId(tenant.qris_image_asset_id ?? null)
    }
  }, [tenant])

  const updatePayment = useMutation({
    mutationFn: async ({
      info, qrisUrl, qrisAssetId,
    }: { info: string; qrisUrl: string | null; qrisAssetId: string | null }) => {
      const { error } = await (supabase
        .from('tenants')
        .update({
          payment_info: info || null,
          qris_image_url: qrisUrl,
          qris_image_asset_id: qrisAssetId,
        } as any) as any)
        .eq('id', tenantId!)
      if (error) throw error
    },
    onSuccess: async () => {
      qc.invalidateQueries({ queryKey: ['tenant-payment-info', tenantId] })
      setSaved(true)
      setTimeout(() => setSaved(false), 3000)
      await triggerRevalidation('tenant-theme')
    },
  })

  if (isLoading) {
    return <p className="text-muted-foreground">Memuat info pembayaran...</p>
  }

  return (
    <div className="space-y-4">
      {/* Instruksi Pembayaran */}
      <div className="rounded-xl border border-border bg-card p-6 space-y-2">
        <label className="text-sm font-medium">Instruksi Pembayaran</label>
        <textarea
          rows={6}
          value={paymentInfo}
          onChange={(e) => setPaymentInfo(e.target.value)}
          placeholder={`Contoh:\nBank BCA\nNo. Rekening: 1234567890\nA.n. Sakura Idol Group\n\nSetelah transfer, upload screenshot bukti pembayaran.`}
          className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none resize-none"
        />
        <p className="text-xs text-muted-foreground">
          Teks ini tampil di halaman booking cheki sebelum fan upload bukti bayar. Kosongkan jika tidak ingin menampilkan info.
        </p>
      </div>

      {/* Gambar QRIS */}
      <div className="rounded-xl border border-border bg-card p-6 space-y-3">
        <div>
          <p className="text-sm font-medium">Gambar QRIS</p>
          <p className="text-xs text-muted-foreground mt-1">
            Gambar ini akan ditampilkan di halaman booking sebagai panduan pembayaran QRIS.
            Upload melalui manajemen aset.
          </p>
        </div>
        {qrisImageUrl ? (
          <div className="flex items-start gap-4">
            <div className="h-36 w-36 shrink-0 rounded-lg border border-border bg-muted overflow-hidden flex items-center justify-center">
              <img src={qrisImageUrl} alt="QRIS" className="max-h-full max-w-full object-contain p-2" />
            </div>
            <div className="flex flex-col gap-2 pt-1">
              <button
                type="button"
                onClick={() => setPickerOpen(true)}
                className="text-sm text-primary hover:underline text-left"
              >
                Ganti Gambar
              </button>
              <button
                type="button"
                onClick={() => { setQrisImageUrl(null); setQrisImageAssetId(null) }}
                className="text-sm text-destructive hover:underline text-left"
              >
                Hapus
              </button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setPickerOpen(true)}
            className="flex items-center gap-2 rounded-md border border-dashed border-border bg-muted/30 px-4 py-3 text-sm text-muted-foreground hover:border-primary/50 hover:text-foreground hover:bg-accent/30 transition-colors"
          >
            <Image className="h-4 w-4" />
            Pilih dari Aset
          </button>
        )}
      </div>

      {/* Save */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => updatePayment.mutate({ info: paymentInfo, qrisUrl: qrisImageUrl, qrisAssetId: qrisImageAssetId })}
          disabled={updatePayment.isPending}
          className="rounded-md bg-primary px-6 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
        >
          {updatePayment.isPending ? 'Menyimpan...' : 'Simpan Perubahan'}
        </button>
        {saved && <p className="text-sm text-green-600">Tersimpan!</p>}
        {updatePayment.error && (
          <p className="text-sm text-destructive">
            {(updatePayment.error as Error).message ?? 'Gagal menyimpan.'}
          </p>
        )}
      </div>

      <MediaPicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onSelect={(assetId, url) => {
          setQrisImageAssetId(assetId)
          setQrisImageUrl(url)
          setPickerOpen(false)
        }}
      />
    </div>
  )
}

// ── Tab: Cheki Grup ───────────────────────────────────────────────────────────

function ChekiGrupTab() {
  const { query, mutation } = useTenantChekiGroupSetting()
  const [saved, setSaved] = useState(false)

  const isEnabled = query.data?.has_cheki_group ?? false

  async function handleToggle(next: boolean) {
    await mutation.mutateAsync(next)
    setSaved(true)
    setTimeout(() => setSaved(false), 3000)
  }

  if (query.isLoading) {
    return <p className="text-muted-foreground">Memuat pengaturan...</p>
  }

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-border bg-card p-6 space-y-4">
        <div>
          <h2 className="font-semibold">Fitur Cheki Grup</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Aktifkan untuk memungkinkan fan memesan cheki foto bersama seluruh grup.
            Setelah diaktifkan, opsi harga cheki grup akan muncul di setiap form cheki yang dibuat.
          </p>
        </div>

        <div className="flex items-center gap-3">
          <button
            type="button"
            role="switch"
            aria-checked={isEnabled}
            onClick={() => handleToggle(!isEnabled)}
            disabled={mutation.isPending}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors disabled:opacity-50 ${
              isEnabled ? 'bg-primary' : 'bg-muted-foreground/30'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                isEnabled ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
          <span className="text-sm font-medium">
            {isEnabled ? 'Aktif' : 'Nonaktif'}
          </span>
          {saved && <p className="text-sm text-green-600">Tersimpan!</p>}
        </div>

        {isEnabled && (
          <div className="rounded-md bg-muted/50 border border-border px-4 py-3 text-sm text-muted-foreground">
            Fitur cheki grup sudah aktif. Buka form cheki manapun dan isi{' '}
            <strong className="text-foreground">Harga Cheki Grup</strong> untuk mengaktifkan
            pilihan grup di form tersebut. Form yang tidak diisi harga grup tetap tidak menampilkan opsi grup.
          </div>
        )}
      </div>

      {mutation.error && (
        <p className="text-sm text-destructive">
          {(mutation.error as Error).message ?? 'Gagal menyimpan.'}
        </p>
      )}
    </div>
  )
}

// ── Tab: Harga Cheki (default prices) ─────────────────────────────────────────

function HargaChekiTab() {
  const { query, mutation } = useTenantChekiDefaultPrices()
  const [saved, setSaved] = useState(false)

  const [priceIdr, setPriceIdr] = useState('')
  const [priceOtsIdr, setPriceOtsIdr] = useState('')
  const [groupPriceIdr, setGroupPriceIdr] = useState('')
  const [groupPriceOtsIdr, setGroupPriceOtsIdr] = useState('')

  useEffect(() => {
    if (!query.data) return
    const d = query.data
    setPriceIdr(d.default_price_idr != null ? String(d.default_price_idr) : '')
    setPriceOtsIdr(d.default_price_ots_idr != null ? String(d.default_price_ots_idr) : '')
    setGroupPriceIdr(d.default_group_price_idr != null ? String(d.default_group_price_idr) : '')
    setGroupPriceOtsIdr(d.default_group_price_ots_idr != null ? String(d.default_group_price_ots_idr) : '')
  }, [query.data])

  async function handleSave() {
    await mutation.mutateAsync({
      default_price_idr: priceIdr ? Number(priceIdr) : null,
      default_price_ots_idr: priceOtsIdr ? Number(priceOtsIdr) : null,
      default_group_price_idr: groupPriceIdr ? Number(groupPriceIdr) : null,
      default_group_price_ots_idr: groupPriceOtsIdr ? Number(groupPriceOtsIdr) : null,
    })
    setSaved(true)
    setTimeout(() => setSaved(false), 3000)
  }

  if (query.isLoading) {
    return <p className="text-muted-foreground">Memuat pengaturan harga...</p>
  }

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-border bg-card p-6 space-y-4">
        <div>
          <h2 className="font-semibold">Harga Default Cheki</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Isi harga di sini untuk dijadikan nilai awal saat membuat form cheki baru.
            Nilai tersebut tetap bisa diedit di masing-masing form.
            Kosongkan field jika tidak ingin ada nilai default.
          </p>
        </div>

        {/* Member cheki prices */}
        <div className="space-y-2">
          <p className="text-sm font-medium">Harga Cheki Member</p>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Harga PO (Rp)</label>
              <input
                type="number"
                min={0}
                step={1000}
                value={priceIdr}
                onChange={(e) => setPriceIdr(e.target.value)}
                placeholder="75000"
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none"
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Harga OTS (Rp)</label>
              <input
                type="number"
                min={0}
                step={1000}
                value={priceOtsIdr}
                onChange={(e) => setPriceOtsIdr(e.target.value)}
                placeholder="100000"
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none"
              />
            </div>
          </div>
        </div>

        {/* Group cheki prices */}
        <div className="space-y-2">
          <p className="text-sm font-medium">Harga Cheki Grup</p>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Harga Grup PO (Rp)</label>
              <input
                type="number"
                min={0}
                step={1000}
                value={groupPriceIdr}
                onChange={(e) => setGroupPriceIdr(e.target.value)}
                placeholder="150000"
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none"
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Harga Grup OTS (Rp)</label>
              <input
                type="number"
                min={0}
                step={1000}
                value={groupPriceOtsIdr}
                onChange={(e) => setGroupPriceOtsIdr(e.target.value)}
                placeholder="200000"
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-3">
        <button
          onClick={handleSave}
          disabled={mutation.isPending}
          className="rounded-md bg-primary px-6 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
        >
          {mutation.isPending ? 'Menyimpan...' : 'Simpan Perubahan'}
        </button>
        {saved && <p className="text-sm text-green-600">Tersimpan!</p>}
        {mutation.error && (
          <p className="text-sm text-destructive">
            {(mutation.error as Error).message ?? 'Gagal menyimpan.'}
          </p>
        )}
      </div>
    </div>
  )
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function ChekiSettingPage() {
  const [activeTab, setActiveTab] = useState<Tab>('payment')

  return (
    <FeatureGate feature="cheki_form">
    <div className="max-w-2xl space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Pengaturan Cheki</h1>
        <p className="text-muted-foreground">
          Kelola info pembayaran, fitur cheki grup, dan harga default cheki.
        </p>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-border">
        {([
          { key: 'payment',     label: 'Info Pembayaran' },
          { key: 'cheki_grup',  label: 'Cheki Grup' },
          { key: 'harga_cheki', label: 'Harga Cheki' },
        ] as { key: Tab; label: string }[]).map(({ key, label }) => (
          <button
            key={key}
            type="button"
            onClick={() => setActiveTab(key)}
            className={`px-5 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px ${
              activeTab === key
                ? 'border-primary text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {activeTab === 'payment' && <PaymentTab />}
      {activeTab === 'cheki_grup' && <ChekiGrupTab />}
      {activeTab === 'harga_cheki' && <HargaChekiTab />}
    </div>
    </FeatureGate>
  )
}
